import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { useForm, Controller } from 'react-hook-form';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { ulid } from 'ulid';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Building, Filter } from 'lucide-react';
import { ConditionEditor } from './ConditionEditor';
import { ConditionCard } from './ConditionCard';
import { validateAllConditionsHaveActions } from '@/lib/conditionValidation';
import { toast } from 'sonner';
import type {
	CreateScenarioMutation,
	CreateScenarioMutationVariables
} from '@/graphql/generated';
import { SCENARIO_CREATE_MUTATION } from './scenarios.gql';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';
import {
	buildCreateScenarioInput,
	type ScenarioConditionFormState
} from './scenario-input';

interface CloneData {
	scenarioId?: string; // Pre-generated ID from clone function
	name?: string;
	conditions?: ScenarioConditionFormState[];
}

interface LocationState {
	cloneData?: CloneData;
}

interface CreateScenarioFormData {
	name: string;
	facilityId?: string;
}

export const ScenariosCreate: React.FC = () => {
	const { t } = useTranslation('scenarios');
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;

	useEffect(() => {
		setPageHeader({
			title: cloneData ? t('clone.title') : t('create.title'),
			description: cloneData
				? 'Create a new scenario based on an existing one'
				: 'Define a new pricing scenario',
			showBackButton: true,
			onBackClick: () => go({ to: '/scenarios' })
		});

		return () => {
			resetPageHeader();
		};
	}, [cloneData, t, go, resetPageHeader, setPageHeader]);

	const [selectedConditions, setSelectedConditions] = useState<
		ScenarioConditionFormState[]
	>(cloneData?.conditions ?? []);
	const [conditionEditorOpen, setConditionEditorOpen] = useState(false);
	const [editingCondition, setEditingCondition] = useState<{
		condition: ScenarioConditionFormState;
		index: number;
	} | null>(null);
	const [conditionOperator, setConditionOperator] = useState<
		'include' | 'exclude'
	>('include');

	const {
		control,
		handleSubmit,
		formState: { errors }
	} = useForm<CreateScenarioFormData>({
		defaultValues: {
			name: cloneData?.name ?? ''
		}
	});

	// Use the pre-generated scenarioId from clone data, or generate a new one
	const idempotentScenarioId = useMemo(
		() => cloneData?.scenarioId ?? ulid(),
		[cloneData?.scenarioId]
	);

	const [createScenario, { loading: isSubmitting }] = useMutation<
		CreateScenarioMutation,
		CreateScenarioMutationVariables
	>(SCENARIO_CREATE_MUTATION);

	useUnsavedChanges(control._formState.isDirty, {
		isSubmitSuccessful: control._formState.isSubmitSuccessful,
		isSubmitting
	});

	const addCondition = (conditionData: ScenarioConditionFormState) => {
		setSelectedConditions(prev => [
			...prev,
			{
				...conditionData,
				conditionId: conditionData.conditionId ?? ulid()
			}
		]);
		setConditionEditorOpen(false);
	};

	const updateCondition = (conditionData: ScenarioConditionFormState) => {
		if (editingCondition) {
			setSelectedConditions(prev =>
				prev.map((c, index) =>
					index === editingCondition.index
						? {
								...conditionData,
								conditionId:
									editingCondition.condition.conditionId ??
									c.conditionId ??
									ulid()
							}
						: c
				)
			);
			setEditingCondition(null);
		}
		setConditionEditorOpen(false);
	};

	const removeCondition = (index: number) => {
		setSelectedConditions(prev => prev.filter((_, i) => i !== index));
	};

	const toggleConditionOperator = (index: number) => {
		setSelectedConditions(prev =>
			prev.map((c, i) =>
				i === index
					? {
							...c,
							operator:
								c.operator === 'include' ? 'exclude' : 'include'
						}
					: c
			)
		);
	};

	const openConditionEditor = (
		operator: 'include' | 'exclude' = 'include'
	) => {
		setConditionOperator(operator);
		setEditingCondition(null);
		setConditionEditorOpen(true);
	};

	const openConditionEditorForEdit = (
		condition: ScenarioConditionFormState,
		index: number
	) => {
		setEditingCondition({ condition, index });
		setConditionOperator(condition.operator);
		setConditionEditorOpen(true);
	};

	const onSubmit = async (data: CreateScenarioFormData) => {
		// Validate that all conditions have meaningful actions
		const validation = validateAllConditionsHaveActions(selectedConditions);

		if (!validation.isValid) {
			toast.error(
				`${t('form.validationErrorPrefix')}${validation.invalidConditions.length > 1 ? 's' : ''} #${validation.invalidConditions.join(', #')} ${validation.invalidConditions.length > 1 ? t('form.validationErrorSuffix') : t('form.validationErrorSuffixSingular')}`,
				{
					duration: 6000
				}
			);
			return;
		}

		try {
			const input = buildCreateScenarioInput(
				{
					name: data.name,
					conditions: selectedConditions
				},
				idempotentScenarioId
			);

			const { data: response } = await createScenario(
				getMutationOptions('scenarios', { input })
			);

			const message = extractCheckResultError(response?.createScenario);

			if (message) {
				showMutationErrorToast('scenarios', 'create', { message });
				return;
			}

			showMutationSuccessToast('scenarios', 'create');
			// Navigate back with the newly created scenario ID for selection
			go({
				to: '/scenarios',
				state: { selectScenarioId: idempotentScenarioId }
			});
		} catch (error) {
			showMutationErrorToast('scenarios', 'create', { error });
		}
	};

	// Separate conditions by operator
	const includeConditions = selectedConditions.filter(
		c => c.operator === 'include'
	);
	const excludeConditions = selectedConditions.filter(
		c => c.operator === 'exclude'
	);

	return (
		<FormPageLayout>
			<FormHeaderWithStatus
				title={cloneData ? t('clone.title') : t('create.title')}
				rightContent={
					<FormActionButtons
						mode={cloneData ? 'clone' : 'create'}
						isSubmitting={isSubmitting}
						disabled={selectedConditions.length === 0}
						formId="scenario-form"
					/>
				}
			/>
			{/* Form */}
			<form id="scenario-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
				{/* Basic Information */}
				<Card>
					<CardTitle className="flex items-center gap-2">
						<Building className="w-5 h-5" />
						{t('form.basicInformation')}
					</CardTitle>
					<CardDescription>
						{t('form.basicInformationDesc')}
					</CardDescription>

					<div className="space-y-2">
						<Label htmlFor="name">
							{t('form.name')} *
						</Label>
						<Controller
							name="name"
							control={control}
							rules={{
								required: t('form.scenarioNameRequired')
							}}
							render={({ field }) => (
								<Input
									{...field}
									id="name"
									placeholder={t(
										'form.scenarioNamePlaceholder'
									)}
									className={
										errors.name
											? 'border-red-500'
											: ''
									}
									autoFocus
								/>
							)}
						/>
						{errors.name && (
							<p className="text-sm text-red-500">
								{errors.name.message}
							</p>
						)}
					</div>
				</Card>

				{/* Conditions Management */}
				<Card>
					<CardTitle className="flex items-center gap-2">
						<Filter className="w-5 h-5" />
						{t('form.scenarioConditions')}
					</CardTitle>
					{/* <CardDescription> */}
					{t('form.scenarioConditionsDesc')}
					{/* </CardDescription> */}

					{/* Action Buttons */}
					<div className="flex gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={() => openConditionEditor('include')}
						>
							<Plus className="w-4 h-4 mr-2" />
							{t('form.addIncludeRules')}
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => openConditionEditor('exclude')}
						>
							<Plus className="w-4 h-4 mr-2" />
							{t('form.addExcludeRules')}
						</Button>
					</div>

					{/* Selected Conditions Display */}
					{selectedConditions.length === 0 ? (
						<div className="py-12 text-center border-2 border-gray-200 border-dashed rounded-lg">
							<Filter className="w-12 h-12 mx-auto mb-4 text-gray-400" />
							<h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">
								{t('form.noRulesConfigured')}
							</h3>
							<p className="mb-4 text-gray-500">
								{t('form.noRulesConfiguredDesc')}
							</p>
							<Button
								type="button"
								variant="outline"
								onClick={() => openConditionEditor('include')}
							>
								<Plus className="w-4 h-4 mr-2" />
								{t('form.configureFirstRules')}
							</Button>
						</div>
					) : (
						<div className="space-y-6">
							{/* Include Conditions */}
							{includeConditions.length > 0 && (
								<div>
									<div className="flex items-center gap-2 mb-4">
										<Badge className="text-green-800 bg-green-100 border-green-200">
											{t('form.includeConditions')} (
											{includeConditions.length})
										</Badge>
										<span className="text-sm text-gray-600">
											{t('form.includeConditionsDesc')}
										</span>
									</div>
									<div className="space-y-3">
										{includeConditions.map(condition => {
											const globalIndex =
												selectedConditions.findIndex(
													c => c === condition
												);
											return (
												<ConditionCard
													key={
														condition.conditionId ??
														globalIndex
													}
													condition={condition}
													onEdit={() =>
														openConditionEditorForEdit(
															condition,
															globalIndex
														)
													}
													onDelete={() =>
														removeCondition(
															globalIndex
														)
													}
													onToggleOperator={() =>
														toggleConditionOperator(
															globalIndex
														)
													}
												/>
											);
										})}
									</div>
								</div>
							)}

							{/* Exclude Conditions */}
							{excludeConditions.length > 0 && (
								<div>
									<div className="flex items-center gap-2 mb-4">
										<Badge className="text-red-800 bg-red-100 border-red-200">
											{t('form.excludeConditions')} (
											{excludeConditions.length})
										</Badge>
										<span className="text-sm text-gray-600">
											{t('form.excludeConditionsDesc')}
										</span>
									</div>
									<div className="space-y-3">
										{excludeConditions.map(condition => {
											const globalIndex =
												selectedConditions.findIndex(
													c => c === condition
												);
											return (
												<ConditionCard
													key={
														condition.conditionId ??
														globalIndex
													}
													condition={condition}
													onEdit={() =>
														openConditionEditorForEdit(
															condition,
															globalIndex
														)
													}
													onDelete={() =>
														removeCondition(
															globalIndex
														)
													}
													onToggleOperator={() =>
														toggleConditionOperator(
															globalIndex
														)
													}
												/>
											);
										})}
									</div>
								</div>
							)}

							{/* Logic Summary */}
							<div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
								<h4 className="mb-2 font-medium">
									{t('form.logicSummary')}
								</h4>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									{t('form.logicSummaryDesc')}
									{includeConditions.length > 0 && (
										<>
											<br />•{' '}
											{t('form.anyOfInclude', {
												count: includeConditions.length
											})}
										</>
									)}
									{excludeConditions.length > 0 && (
										<>
											<br />•{' '}
											{t('form.noneOfExclude', {
												count: excludeConditions.length
											})}
										</>
									)}
									{selectedConditions.length === 0 && (
										<>
											<br />•{' '}
											{t('form.noConditionsWillNotApply')}
										</>
									)}
								</p>
							</div>
						</div>
					)}
				</Card>
			</form>

			{/* Condition Editor Dialog */}
			<ConditionEditor
				open={conditionEditorOpen}
				mode={editingCondition ? 'edit' : 'create'}
				condition={editingCondition?.condition}
				operator={conditionOperator}
				onSave={editingCondition ? updateCondition : addCondition}
				onCancel={() => {
					setConditionEditorOpen(false);
					setEditingCondition(null);
				}}
			/>
		</FormPageLayout>
	);
};
